import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ConfirmAction from "@/components/ConfirmAction";
import { Card, Button, Chip, Skeleton, Toast } from "@/ui";

export default function ImportProducts() {
  const { shopId, shopName } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setLoading(true); setError(""); setPreview(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/import/preview?shopId=${shopId}`, {
        method: "POST", body: form, credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "preview");
      setPreview(data);
    } catch (err) { setError(err.message === "preview" ? "We could not read that file. Save it as CSV or Excel and try again." : err.message); }
    finally { setLoading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const okRows = preview?.rows?.filter((r) => r.ok) || [];

  const commit = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post("/products/import/commit", { shopId, rows: okRows });
      setToast(`brought in ${r.data.created} item${r.data.created === 1 ? "" : "s"}`);
      setTimeout(() => navigate("/products"), 900);
    } catch (e) { setError(friendlyError(e)); setBusy(false); setConfirm(false); }
  };

  return (
    <Layout shopName={shopName}>
      <div className="row" style={{ marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" data-testid="import-back" onClick={() => navigate("/products")}>
          <ArrowLeft size={20} strokeWidth={1.75} />back to items
        </button>
      </div>
      <h1 className="t-h1" style={{ marginBottom: 4 }}>import items</h1>
      <p className="muted" style={{ marginBottom: 20, maxWidth: 620 }}>
        Bring in many items at once from a spreadsheet. We'll show you exactly what will be created and flag any problems first — nothing is saved until you say so.
      </p>
      {error ? <div className="err-text" data-testid="import-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      <Card style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span className="ph-icon" style={{ margin: 0 }}><FileSpreadsheet size={18} strokeWidth={1.75} /></span>
          <div style={{ flex: 1, minWidth: 180 }}>
            <p className="t-body">{fileName || "choose a CSV or Excel file"}</p>
            <p className="muted-3 t-small">columns: name, price, category, sizes, flavours, on hand</p>
          </div>
          <Button variant="primary" data-testid="choose-file-btn" onClick={() => fileRef.current?.click()}>
            <Upload size={16} strokeWidth={1.75} />choose file
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv" data-testid="import-file-input" style={{ display: "none" }} onChange={onPick} />
        </div>
      </Card>

      {loading ? <div className="tbl-wrap"><div style={{ padding: 16 }} className="stack"><Skeleton /><Skeleton /></div></div> : null}

      {preview ? (
        <>
          <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <Chip tone="good" dot data-testid="import-ok-count"><CheckCircle2 size={13} strokeWidth={2} />{preview.summary.ok} ready</Chip>
            {preview.summary.withProblems ? <Chip tone="warn" dot data-testid="import-problem-count"><AlertTriangle size={13} strokeWidth={2} />{preview.summary.withProblems} with problems</Chip> : null}
          </div>
          <div className="tbl-wrap" data-testid="import-preview-table">
            <table className="tbl">
              <thead><tr><th>Row</th><th>Name</th><th className="num">Price</th><th>Category</th><th>Sizes &amp; flavours</th><th className="num">On hand</th><th>Status</th></tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row} data-testid={`import-row-${r.row}`} style={{ background: r.ok ? undefined : "var(--bad-weak, rgba(200,60,60,0.06))" }}>
                    <td className="muted-3">{r.row}</td>
                    <td>{r.name || <span className="muted-3">—</span>}</td>
                    <td className="num">{r.price || <span className="muted-3">—</span>}</td>
                    <td className="muted-3 t-small">{r.category}</td>
                    <td className="t-small">{r.variants.map((v) => `${v.name}`).join(", ") || <span className="muted-3">—</span>}</td>
                    <td className="num">{r.onHand}</td>
                    <td>{r.ok ? <Chip tone="good" dot>ready</Chip> : <span className="err-text t-small">{r.problems.join("; ")}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row-between" style={{ marginTop: 16 }}>
            <p className="muted t-small">{preview.summary.withProblems ? "rows with problems will be skipped." : "everything looks good."}</p>
            <Button variant="primary" data-testid="import-confirm-btn" disabled={okRows.length === 0} onClick={() => setConfirm(true)}>
              bring in {okRows.length} item{okRows.length === 1 ? "" : "s"}
            </Button>
          </div>
        </>
      ) : null}

      <ConfirmAction
        open={confirm}
        title={`Bring in ${okRows.length} item${okRows.length === 1 ? "" : "s"}?`}
        preview={[
          `${okRows.length} item${okRows.length === 1 ? "" : "s"} will be created in your shop.`,
          preview?.summary?.withProblems ? `${preview.summary.withProblems} row${preview.summary.withProblems === 1 ? "" : "s"} with problems will be skipped.` : "No problems found.",
          "You can edit or hide any item afterwards.",
        ]}
        confirmLabel="Yes, bring them in" busy={busy} error={error}
        onConfirm={commit} onCancel={() => setConfirm(false)}
      />
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="import-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
